import type { TemplateStudioLibraryImage } from "@shared/schema";
import type { TopPerformerMetric } from "@shared/creative-performance";

export const MAX_BOARD_REFERENCES = 8;

export type PerformanceSnapshotInput = {
  metric: TopPerformerMetric;
  value: number;
  spend: number;
  costPer: number | null;
  since: string;
  until: string;
};

export type ReferenceSelection = {
  referenceIds: string[];
  explicit: boolean;
};

export type FrozenBoardSubmission = { key: string; body: string };
export type SelectionContextVersion = { context: string; generation: number };

export function freezeBoardSubmission(key: string, body: unknown): FrozenBoardSubmission {
  return { key, body: JSON.stringify(body) };
}

export function advanceSelectionContext(
  previous: SelectionContextVersion,
  context: string,
): SelectionContextVersion {
  return previous.context === context
    ? previous
    : { context, generation: previous.generation + 1 };
}

export function sameSelectionContext(
  current: SelectionContextVersion,
  captured: SelectionContextVersion,
): boolean {
  return current.context === captured.context && current.generation === captured.generation;
}

export function beginExplicitReferenceSelection(
  selection: ReferenceSelection,
  typedReferences: readonly Pick<TemplateStudioLibraryImage, "id">[],
): ReferenceSelection {
  if (selection.explicit) return selection;
  return {
    referenceIds: typedReferences.length <= MAX_BOARD_REFERENCES
      ? typedReferences.map((reference) => reference.id)
      : [],
    explicit: true,
  };
}

export function selectedReferenceCount(
  selection: ReferenceSelection,
  typedReferences: readonly Pick<TemplateStudioLibraryImage, "id">[],
): number {
  return new Set(
    selection.explicit
      ? selection.referenceIds
      : typedReferences.length <= MAX_BOARD_REFERENCES
        ? typedReferences.map((reference) => reference.id)
        : [],
  ).size;
}

export function toggleLibraryReference(
  selection: ReferenceSelection,
  typedReferences: readonly Pick<TemplateStudioLibraryImage, "id">[],
  referenceId: string,
  checked: boolean,
): ReferenceSelection {
  const explicit = beginExplicitReferenceSelection(selection, typedReferences);
  const next = new Set(explicit.referenceIds);
  if (checked) {
    if (!next.has(referenceId) && next.size >= MAX_BOARD_REFERENCES) return explicit;
    next.add(referenceId);
  } else {
    next.delete(referenceId);
  }
  return { explicit: true, referenceIds: [...next] };
}

export function boardReferenceIdsForAdmission(
  selection: ReferenceSelection,
  typedReferenceCount: number,
): string[] | undefined {
  if (!selection.explicit && typedReferenceCount <= MAX_BOARD_REFERENCES) return undefined;
  return [...selection.referenceIds];
}

export function formatPerformanceMetricLabel(
  snapshot: Pick<PerformanceSnapshotInput, "metric" | "value" | "spend" | "costPer">,
): string {
  const noun = snapshot.metric === "leads"
    ? "lead"
    : snapshot.metric === "checkouts"
      ? "checkout"
      : "purchase";
  const count = snapshot.value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  const cost = snapshot.costPer === null
    ? "cost unavailable"
    : `$${snapshot.costPer.toFixed(2)} per ${noun}`;
  return `${count} ${noun}${snapshot.value === 1 ? "" : "s"}, ${cost}, $${snapshot.spend.toFixed(2)} spent`;
}